import React, { useCallback, useState, useRef, useEffect, useMemo, forwardRef, useContext } from 'react';
import { Alert, Pressable, Text, View, Image, Platform, Linking, KeyboardAvoidingView, Keyboard } from 'react-native';
import { store, DefaultRootState } from '@reduxLocal/persistState';
import { HIDE_TOOLBAR, SHOW_TOOLBAR } from '@reduxLocal/actions/types.js';
import { useNavigation } from '@react-navigation/native';
import RobotHelper from '@components/RobotHelper/RobotHelper';
import styles from '@stylesheet';
import Handlebars, { compile } from 'handlebars';
import AnimatedLottieView from '@utils/lottieAnimation';
import emailBody from '@utils/html/defaultHtml';
import apis from '@api/api';
import { RichEditorProps } from 'react-native-pell-rich-editor/index';
import DefaultEditor, { gjsOptions } from '@components/Global/EmailBuilder/CustomEditor';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import grapesjs, { usePlugin } from 'grapesjs';
import SmartSaaSPlugin from '@components/Global/EmailBuilder/Components/SmartSaaSPlugin';
import AssetPlugin from '@components/Global/EmailBuilder/Components/AssetPlugin';
import basic from 'grapesjs-blocks-basic';
import flexbox from 'grapesjs-blocks-flexbox';
import buttonplugin from '@components/Global/EmailBuilder/Components/ButtonPlugin';
import DynamicTextPlugin from '@components/Global/EmailBuilder/Components/DynamicTextPlugin';
import ShareLinksPlugin, { createShareLinkFetchers } from '@components/Global/EmailBuilder/Components/ShareLinksPlugin';
import GjsEditor, { AssetsProvider, Canvas, useEditor } from '@grapesjs/react';
import 'grapesjs/dist/css/grapes.min.css';

const CreateEmailTemplate = ({ route }) => {
  const templateId = route.params?.templateId;
  const [showGlobalAssets, setShowGlobalAssets] = useState(false);
  const navigation = useNavigation();
  const [canGoBack, setCanGoBack] = useState(false);
  const editorRef = useRef<RichEditorProps>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [flip, setFlip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editorState, setEditorState] = useState(null);
  const [previewState, setPreviewState] = useState();
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateTags, setTemplateTags] = useState([]);
  const queryClient = useQueryClient();
  const [initialState, setInitialState] = useState(null);
  const dispatch = useDispatch();
  const selectedProfileId = useSelector((state: DefaultRootState) => state.app.selectedProfile?.id);
  const fallbackUserId = useSelector((state: DefaultRootState) => state.promiseData?.data?.userData?.id);
  const shareLinkFetchers = useMemo(
    () => createShareLinkFetchers({ userId: selectedProfileId || fallbackUserId }),
    [selectedProfileId, fallbackUserId],
  );
  const [gjsOptionsState, setGjsOptions] = useState(gjsOptions);
  const [grapesjsCss, setGrapesjsCss] = useState('');



  useEffect(() => {

    if (initialState && (initialState.content || initialState.content?.html)) {
      const content = initialState.content || initialState.content?.html;
      let newGjsOptions = { ...gjsOptionsState };
      if (initialState.projectData) {
        console.log('found project data', initialState.projectData);
        newGjsOptions.projectData = initialState.projectData;
        setGjsOptions(newGjsOptions);
      } else {
        // newGjsOptions.projectData.assets = assets;
        newGjsOptions.projectData.pages = [{
          name: initialState.title,
          component: (initialState.content || initialState.content?.html),
        }]
      }
    }
    return () => {
      setGjsOptions(gjsOptions);
    }
  }, [initialState]);

  useEffect(() => {
    if (templateId) {
      const template = queryClient.getQueryData(['emailTemplate' + templateId]);
      setInitialState(template);
    }
  }, [templateId])


  const handleHideGlobalAssets = useCallback(() => { setShowGlobalAssets(false) }, [showGlobalAssets]);

  const _handleSaveTemplate = useCallback(async () => {
    //TODO handle id assignment to company if company
    await compileEmail().then(async (res) => {
      const emailTemplate = { templateString: res?.toString(), templateTitle: templateTitle, templateDescription: templateDescription, templateTags: templateTags };

      await apis.saveEmailTemplate(emailTemplate).then(() => {
        alert('Email template saved successfully')
      })
    }
    );
  }, [editorRef, editorState, templateTitle, templateDescription]);



  const compileEmail = useCallback(async () => {
    // Set the loading state to true.
    try {
      // Import the HTML template using a correct path.
      const htmlTemplate = emailBody; // Use .default to access the string content

      // Compile the HTML template using Handlebars.
      const template = Handlebars.compile(htmlTemplate);

      // Render the compiled template with the editorState.
      const returnHtml = template({ body: editorState });

      // Set the loading state to false when the operation is complete.
      return returnHtml;
    } catch (e) {
      // Handle any errors that occur during compilation.
      console.error(e);
      if (__DEV__) {
        alert(e);
      }
      // Set the loading state to false in the error case as well.
    }
  }, [editorState, previewState, flip]);


  const handleBeforeRemove = (e) => {
    if (!canGoBack) {
      e.preventDefault();
      console.warn('preventing page change');
      if (Platform.OS === 'web') {
        //Create alert for web
        if (window.confirm('You have unsaved changes. Are you sure to discard them and leave the screen?')) {
          navigation.dispatch(e.data.action);
          dispatch({ type: HIDE_TOOLBAR });
        }
      } else {
        Alert.alert(
          'Discard changes?',
          'You have unsaved changes. Are you sure to discard them and leave the screen?',
          [
            { text: "Don't leave", style: 'cancel', onPress: () => { } },
            {
              text: 'Discard',
              style: 'destructive',
              onPress: () => {
                navigation.dispatch(e.data.action);
                dispatch({ type: HIDE_TOOLBAR });
              },
            },
          ]
        );
      }
    }
  };

  useEffect(
    () => {
      const beforeRemoveListener = navigation.addListener('beforeRemove', handleBeforeRemove);

      return () => {
        beforeRemoveListener();
      }
    },
    [navigation, editorRef]
  );

  const _handleRequestAI = async () => {
    await apis.genEmail({ prompt: "Create a html email template with a product description of health device" }).then((res => {
      console.log(res);
    }))
  }

  return (
    <View style={{ flex: 1, paddingLeft: 90, alignSelf: 'stretch', minHeight: "100%" }}>
      <View style={[styles.face, { flex: 1, gap: 30, alignSelf: "stretch", minHeight: "100%" }]}>
        {
          loading ?
            <AnimatedLottieView source={require('@assets/animations/leaf_Loader.json')} style={{ width: '100%', height: '100%' }} autoPlay />
            :
            <GjsEditor
              grapesjs={grapesjs}
              grapesjsCss={grapesjsCss}
              options={gjsOptionsState}
              plugins={[
                usePlugin(SmartSaaSPlugin, {}),
                usePlugin(ShareLinksPlugin, { fetchers: shareLinkFetchers }),
                usePlugin(AssetPlugin, {}),
                usePlugin(buttonplugin, {}),
                usePlugin(DynamicTextPlugin, {}),
                basic,
                flexbox,
              ]}
            >
              <DefaultEditor type={"email"} loadCallback={setGjsOptions} initialData={initialState} />
            </GjsEditor>
        }
      </View>
      {
        showHelp &&
        <View style={{ position: 'absolute', flex: 1, justifyContent: 'flex-end', alignSelf: 'flex-end', width: Platform.OS != 'web' ? '70%' : undefined, left: 50, height: 300, top: -50 }}>
          <RobotHelper helpText={['Replace any custom fields with {{}}', 'You can easily import your image assets with global assets']} setState={setShowHelp} />
        </View>
      }
    </View >
  )
}
export default CreateEmailTemplate;